import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Link,
  Skeleton,
  Tooltip,
} from "@mui/material";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import ContactMailOutlined from "@mui/icons-material/ContactMailOutlined";

import agent from "../../utils/axios";
import { capitalizeFirstLetter } from "../../utils/text";
import { EMPTY_VALUE, formatMoney } from "../../utils/format";
import {
  Callout,
  KpiTile,
  MaskedTaxId,
  PageHeader,
  StatusChip,
  payableMeta,
} from "../../components/ui";
import PayeeBills, { BillDataItem } from "./PayeeBills";
import { payeeTotals, yearInTimeZone } from "./payeeTotals";

/**
 * GET /api/payees/{id}. Only the fields this page shows are typed; the tax identifier is
 * never read here — the Tax card uses the server's `last4` only.
 */
type PayeeData = {
  id: string;
  name?: {
    first?: string | null;
    middle?: string | null;
    last?: string | null;
  } | null;
  mailingAddress?: {
    address1?: string | null;
    address2?: string | null;
    address3?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  } | null;
  phone?: {
    home?: string | null;
    cell?: string | null;
    work?: string | null;
  } | null;
  last4?: string | null;
  email?: string | null;
  type?: string | null;
  payable?: boolean | null;
  reasonNotPayable?: string | null;
  paymentMethod?: string | null;
  bills?: BillDataItem[] | null;
};

const BREADCRUMB_ROOT = { label: "Payees", to: "/payees" };

const fullNameOf = (payee: PayeeData): string =>
  [payee.name?.first, payee.name?.middle, payee.name?.last]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");

/** A label/value list (dl) laid out as a responsive grid. */
function FieldList({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        columnGap: 6,
        rowGap: 4,
        m: 0,
      }}
    >
      {children}
    </Box>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        component="dt"
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "text.secondary",
        }}
      >
        {label}
      </Box>
      <Box component="dd" sx={{ m: 0, mt: 1, overflowWrap: "anywhere" }}>
        {children}
      </Box>
    </Box>
  );
}

const valueOrDash = (value?: string | null) =>
  value && value.trim() ? value : EMPTY_VALUE;

function AddressLines({ payee }: { payee: PayeeData }) {
  const a = payee.mailingAddress;
  const street = [a?.address1, a?.address2, a?.address3]
    .map((part) => (part ?? "").trim())
    .filter(Boolean);
  const region = [(a?.state ?? "").trim(), (a?.postalCode ?? "").trim()]
    .filter(Boolean)
    .join(" ");
  const cityLine = [(a?.city ?? "").trim(), region].filter(Boolean).join(", ");
  const lines = [...street, cityLine].filter(Boolean);
  if (lines.length === 0) return <>{EMPTY_VALUE}</>;
  return (
    <>
      {lines.map((line, index) => (
        <Box component="span" key={index} sx={{ display: "block" }}>
          {line}
        </Box>
      ))}
    </>
  );
}

function LoadingState() {
  return (
    <>
      <PageHeader
        title={<Skeleton width={280} />}
        subtitle={<Skeleton width={220} />}
        helmetTitle="Payee"
        breadcrumbs={[BREADCRUMB_ROOT, { label: "Loading" }]}
      />
      <Box
        aria-busy="true"
        aria-label="Loading payee"
        sx={{ display: "grid", gap: 4 }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 4,
          }}
        >
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} variant="rectangular" height={78} />
          ))}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
            gap: 4,
          }}
        >
          <Skeleton variant="rectangular" height={180} />
          <Skeleton variant="rectangular" height={180} />
        </Box>
        <Skeleton variant="rectangular" height={240} />
      </Box>
    </>
  );
}

function PayeeDetails() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const Status = searchParams.get("Status");

  const [payeeData, setPayeeData] = useState<PayeeData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setPayeeData(null);
    setLoadError(false);
    agent.Payees.getPayee(id ?? "")
      .then((response: PayeeData | null) => {
        if (!active) return;
        // An empty answer would otherwise leave the page loading for ever.
        if (response) setPayeeData(response);
        else setLoadError(true);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });
    return () => {
      active = false;
    };
  }, [id, reloadKey]);

  const retry = useCallback(() => setReloadKey((key) => key + 1), []);

  const clearStatusFilter = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("Status");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const currentYear = useMemo(
    () => yearInTimeZone(new Date()) ?? new Date().getFullYear(),
    []
  );

  const allBills = useMemo(() => payeeData?.bills ?? [], [payeeData]);
  // KPIs always use every bill, not the ?Status= subset.
  const totals = useMemo(
    () => payeeTotals(allBills, currentYear),
    [allBills, currentYear]
  );

  if (loadError) {
    return (
      <>
        <PageHeader
          title="Payee"
          helmetTitle={`Payee Details - ${id ?? ""}`}
          breadcrumbs={[BREADCRUMB_ROOT, { label: id ?? "Payee" }]}
        />
        <Callout
          tone="err"
          role="alert"
          title="Couldn't load this payee"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Retry
            </Button>
          }
        >
          Please retry, or contact support if this continues.
        </Callout>
      </>
    );
  }

  if (payeeData === null) {
    return <LoadingState />;
  }

  // AB#5377: paymentService is optional on a bill — the ops repair tooling has created bills
  // without one — so an unguarded read here threw and blanked the whole payee page whenever it
  // was opened from a status drill-down.
  const filteredBills = Status
    ? allBills.filter((item) => item.paymentService?.paymentStatus === Status)
    : allBills;

  const fullName = fullNameOf(payeeData) || "Unnamed payee";
  const firstName = (payeeData.name?.first ?? "").trim() || fullName;
  const type = (payeeData.type ?? "").trim();
  const payable = payableMeta(payeeData.payable, payeeData.reasonNotPayable);
  const reason = (payeeData.reasonNotPayable ?? "").trim();
  const reasonSentence =
    reason && !/[.!?]$/.test(reason) ? `${reason}.` : reason;

  return (
    <React.Fragment>
      <PageHeader
        title={fullName}
        subtitle={`Payee ${payeeData.id ?? id}${
          type ? ` · ${capitalizeFirstLetter(type)}` : ""
        }`}
        helmetTitle={`Payee Details - ${id ?? ""}`}
        breadcrumbs={[BREADCRUMB_ROOT, { label: fullName }]}
        actions={
          <Tooltip
            title={payeeData.payable === false ? payable.label : ""}
            placement="bottom-end"
          >
            <Box sx={{ maxWidth: 360, display: "inline-flex", minWidth: 0 }}>
              <StatusChip meta={payable} />
            </Box>
          </Tooltip>
        }
      />

      <Box sx={{ display: "grid", gap: 4 }}>
        {payeeData.payable === false && (
          <Callout tone="warn" title={`CSI Pay can't pay ${firstName} by ACH`}>
            {[reasonSentence, "New ACH bills are converted to checks."]
              .filter(Boolean)
              .join(" ")}
          </Callout>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 4,
          }}
        >
          <KpiTile
            tone="ok"
            value={formatMoney(totals.paidInYear)}
            label={`Paid in ${currentYear}`}
          />
          <KpiTile tone="brand" value={totals.billCount} label="Bills" />
          <KpiTile
            tone="warn"
            value={formatMoney(totals.waitingToBePaid)}
            label="Waiting to be paid"
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
            gap: 4,
            alignItems: "start",
          }}
        >
          <Card>
            <CardHeader
              avatar={<ContactMailOutlined sx={{ color: "text.secondary" }} />}
              title="Contact"
            />
            <Divider />
            <CardContent>
              <FieldList>
                <Field label="Type">
                  {type ? capitalizeFirstLetter(type) : EMPTY_VALUE}
                </Field>
                <Field label="Email">
                  {payeeData.email ? (
                    <Link href={`mailto:${payeeData.email}`}>
                      {payeeData.email}
                    </Link>
                  ) : (
                    EMPTY_VALUE
                  )}
                </Field>
                <Field label="Home phone">
                  {valueOrDash(payeeData.phone?.home)}
                </Field>
                <Field label="Cell phone">
                  {valueOrDash(payeeData.phone?.cell)}
                </Field>
                <Field label="Work phone">
                  {valueOrDash(payeeData.phone?.work)}
                </Field>
                <Field label="Address">
                  <AddressLines payee={payeeData} />
                </Field>
                <Field label="Payment method">
                  {valueOrDash(payeeData.paymentMethod)}
                </Field>
              </FieldList>
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              avatar={<BadgeOutlined sx={{ color: "text.secondary" }} />}
              title="Tax"
            />
            <Divider />
            <CardContent>
              <FieldList>
                <Field label="Tax ID">
                  <MaskedTaxId
                    value={payeeData.last4 ? `•••• ${payeeData.last4}` : null}
                    canReveal={false}
                  />
                </Field>
              </FieldList>
            </CardContent>
          </Card>
        </Box>

        <PayeeBills
          billData={filteredBills}
          emptyMessage={
            Status
              ? `No bills with payment status ${Status}.`
              : "No bills for this payee yet."
          }
          toolbar={
            Status ? (
              <Chip
                variant="outlined"
                label={`Showing: ${Status}`}
                onDelete={clearStatusFilter}
                aria-label={`Showing: ${Status}. Press Delete to show every bill.`}
                sx={{ mb: 1 }}
              />
            ) : undefined
          }
        />
      </Box>
    </React.Fragment>
  );
}

export default PayeeDetails;
